/**
 * Active filters display component.
 *
 * Shows currently active filters as removable chips.
 * Displays localized labels for filter IDs from facet values.
 */

'use client';

import type { FacetResults, FilterState } from '@/lib/algolia';
import { getBaseFacetName } from '@/lib/algolia/facets';
import { getLabelForId } from '@/lib/algolia/facetValues';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import styles from './ActiveFilters.module.scss';

interface ActiveFiltersProps {
  filters: FilterState;
  facets?: FacetResults; // Needed to look up localized labels
}

/**
 * All supported filter parameter names for active filter chips.
 */
type FilterChipParamKey =
  // Watch filters
  | 'collection'
  | 'size'
  | 'case'
  | 'dial-color'
  | 'type'
  // Strap filters
  | 'material'
  | 'color'
  | 'lug'
  | 'lug-type'
  | 'buckle'
  // Eyewear filters
  | 'frame-material'
  | 'lens-color'
  // Common
  | 'price';

interface FilterChip {
  key: string;
  label: string;
  value: string;
  displayValue: string;
  paramKey: FilterChipParamKey;
}

export function ActiveFilters({ filters, facets = {} }: ActiveFiltersProps) {
  const { toggleFilterValue, setParams, clearFilters, hasActiveFilters } = useSearchParams();

  if (!hasActiveFilters) return null;

  // Helper to get localized label from facets
  const getLabel = (id: string, facetBaseName: string): string => {
    // Find the facet values for this facet
    const facetKey = Object.keys(facets).find((key) => getBaseFacetName(key) === facetBaseName);
    if (facetKey && facets[facetKey]) {
      return getLabelForId(id, facets[facetKey], facetBaseName);
    }
    return id;
  };

  // Build list of active filter chips
  const chips: FilterChip[] = [];

  filters.collection?.forEach((id) => {
    const displayValue = getLabel(id, 'collection');
    chips.push({
      key: `collection-${id}`,
      label: 'Collection',
      value: id,
      displayValue,
      paramKey: 'collection',
    });
  });

  filters.size?.forEach((id) => {
    chips.push({
      key: `size-${id}`,
      label: 'Size',
      value: id,
      displayValue: `${id}mm`,
      paramKey: 'size',
    });
  });

  filters.case?.forEach((id) => {
    const displayValue = getLabel(id, 'case');
    chips.push({
      key: `case-${id}`,
      label: 'Case',
      value: id,
      displayValue,
      paramKey: 'case',
    });
  });

  filters.dialColor?.forEach((id) => {
    const displayValue = getLabel(id, 'dial_color');
    chips.push({
      key: `dial-color-${id}`,
      label: 'Dial',
      value: id,
      displayValue,
      paramKey: 'dial-color',
    });
  });

  filters.type?.forEach((id) => {
    const displayValue = getLabel(id, 'type');
    chips.push({
      key: `type-${id}`,
      label: 'Type',
      value: id,
      displayValue,
      paramKey: 'type',
    });
  });

  // ============================================
  // Strap filter chips
  // ============================================

  filters.material?.forEach((id) => {
    const displayValue = getLabel(id, 'material');
    chips.push({
      key: `material-${id}`,
      label: 'Material',
      value: id,
      displayValue,
      paramKey: 'material',
    });
  });

  filters.color?.forEach((id) => {
    const displayValue = getLabel(id, 'color');
    chips.push({
      key: `color-${id}`,
      label: 'Color',
      value: id,
      displayValue,
      paramKey: 'color',
    });
  });

  filters.lug?.forEach((id) => {
    chips.push({
      key: `lug-${id}`,
      label: 'Lug Width',
      value: id,
      displayValue: `${id}mm`,
      paramKey: 'lug',
    });
  });

  filters.lugType?.forEach((id) => {
    const displayValue = getLabel(id, 'lug_type');
    chips.push({
      key: `lug-type-${id}`,
      label: 'Lug Type',
      value: id,
      displayValue,
      paramKey: 'lug-type',
    });
  });

  filters.buckle?.forEach((id) => {
    const displayValue = getLabel(id, 'buckle');
    chips.push({
      key: `buckle-${id}`,
      label: 'Buckle',
      value: id,
      displayValue,
      paramKey: 'buckle',
    });
  });

  // ============================================
  // Eyewear filter chips
  // ============================================

  filters.frameMaterial?.forEach((id) => {
    const displayValue = getLabel(id, 'frame_material');
    chips.push({
      key: `frame-material-${id}`,
      label: 'Frame Material',
      value: id,
      displayValue,
      paramKey: 'frame-material',
    });
  });

  filters.lensColor?.forEach((id) => {
    const displayValue = getLabel(id, 'lens_color');
    chips.push({
      key: `lens-color-${id}`,
      label: 'Lens Color',
      value: id,
      displayValue,
      paramKey: 'lens-color',
    });
  });

  if (filters.priceMin !== undefined || filters.priceMax !== undefined) {
    const min = filters.priceMin ?? 0;
    const max = filters.priceMax;
    const priceLabel = max
      ? `$${min.toLocaleString()} – $${max.toLocaleString()}`
      : `$${min.toLocaleString()}+`;
    chips.push({
      key: 'price',
      label: 'Price',
      value: 'price',
      displayValue: priceLabel,
      paramKey: 'price',
    });
  }

  const handleRemove = (chip: FilterChip) => {
    if (chip.paramKey === 'price') {
      setParams({ 'price-min': null, 'price-max': null, page: 1 });
    } else {
      toggleFilterValue(chip.paramKey, chip.value);
    }
  };

  return (
    <div className={styles.activeFilters}>
      <div className={styles.chips}>
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={() => handleRemove(chip)}
            className={styles.chip}
            aria-label={`Remove ${chip.label}: ${chip.displayValue}`}
          >
            <span className={styles.chipLabel}>{chip.label}:</span>
            <span className={styles.chipValue}>{chip.displayValue}</span>
            <svg
              className={styles.removeIcon}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ))}
      </div>

      {chips.length >= 1 && (
        <button type="button" onClick={clearFilters} className={styles.clearAll}>
          Clear All
        </button>
      )}
    </div>
  );
}
